// Ghost of the personal best (RALLY_GOAL.md §5 P1-2): the car's pose sampled at 10 Hz of stage
// time — positions and rotations, not inputs, so a ghost doesn't depend on cross-browser
// determinism — stored in localStorage beside the best time and replayed with interpolation.

/** Samples per second of stage time. */
export const GHOST_RATE = 10;
/** Floats per sample: time, position (3), rotation quaternion (4). */
const STRIDE = 8;

export interface GhostPose {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
}

export class GhostRecorder {
  private data = new Float32Array(STRIDE * 600);
  private n = 0;
  private next = 0;

  reset(): void {
    this.n = 0;
    this.next = 0;
  }

  get samples(): number {
    return this.n;
  }

  /** Call every frame while the clock runs; keeps one sample per 1 / GHOST_RATE s (and `force`). */
  sample(t: number, p: GhostPose, force = false): void {
    if (!force && t < this.next) return;
    // Fixed schedule (not "last sample + period"), so frame timing doesn't drift the rate.
    while (this.next <= t) this.next += 1 / GHOST_RATE;
    if ((this.n + 1) * STRIDE > this.data.length) {
      const grown = new Float32Array(this.data.length * 2);
      grown.set(this.data);
      this.data = grown;
    }
    const o = this.n * STRIDE;
    const d = this.data;
    d[o] = t;
    d[o + 1] = p.x;
    d[o + 2] = p.y;
    d[o + 3] = p.z;
    d[o + 4] = p.qx;
    d[o + 5] = p.qy;
    d[o + 6] = p.qz;
    d[o + 7] = p.qw;
    this.n++;
  }

  track(): GhostTrack {
    return new GhostTrack(this.data.slice(0, this.n * STRIDE));
  }
}

export class GhostTrack {
  constructor(readonly data: Float32Array) {}

  get duration(): number {
    return this.data.length >= STRIDE ? this.data[this.data.length - STRIDE] : 0;
  }

  get samples(): number {
    return this.data.length / STRIDE;
  }

  /** Pose at stage time t (s): position lerped, rotation slerped between neighbouring samples. */
  poseAt(t: number, out: GhostPose): boolean {
    const d = this.data;
    const n = d.length / STRIDE;
    if (n < 2 || t < d[0] || t > d[(n - 1) * STRIDE]) return false;
    // Samples are ~evenly spaced: start from the estimate and walk.
    let i = Math.min(n - 2, Math.max(0, Math.floor((t - d[0]) * GHOST_RATE)));
    while (i > 0 && d[i * STRIDE] > t) i--;
    while (i < n - 2 && d[(i + 1) * STRIDE] < t) i++;
    const a = i * STRIDE;
    const b = a + STRIDE;
    const span = d[b] - d[a];
    const f = span > 1e-6 ? Math.min(1, Math.max(0, (t - d[a]) / span)) : 0;
    out.x = d[a + 1] + (d[b + 1] - d[a + 1]) * f;
    out.y = d[a + 2] + (d[b + 2] - d[a + 2]) * f;
    out.z = d[a + 3] + (d[b + 3] - d[a + 3]) * f;
    // Slerp (shortest arc).
    let bx = d[b + 4];
    let by = d[b + 5];
    let bz = d[b + 6];
    let bw = d[b + 7];
    const ax = d[a + 4];
    const ay = d[a + 5];
    const az = d[a + 6];
    const aw = d[a + 7];
    let cos = ax * bx + ay * by + az * bz + aw * bw;
    if (cos < 0) {
      cos = -cos;
      bx = -bx;
      by = -by;
      bz = -bz;
      bw = -bw;
    }
    let ka = 1 - f;
    let kb = f;
    if (cos < 0.9995) {
      const th = Math.acos(cos);
      const s = Math.sin(th);
      ka = Math.sin((1 - f) * th) / s;
      kb = Math.sin(f * th) / s;
    }
    out.qx = ax * ka + bx * kb;
    out.qy = ay * ka + by * kb;
    out.qz = az * ka + bz * kb;
    out.qw = aw * ka + bw * kb;
    const l = Math.hypot(out.qx, out.qy, out.qz, out.qw) || 1;
    out.qx /= l;
    out.qy /= l;
    out.qz /= l;
    out.qw /= l;
    return true;
  }

  encode(): string {
    const u = new Uint8Array(this.data.buffer, this.data.byteOffset, this.data.byteLength);
    let s = '';
    for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
    return btoa(s);
  }

  static decode(b64: string): GhostTrack | null {
    try {
      const s = atob(b64);
      if (s.length % (STRIDE * 4) !== 0) return null;
      const u = new Uint8Array(s.length);
      for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
      const d = new Float32Array(u.buffer);
      for (const v of d) if (!Number.isFinite(v)) return null;
      return new GhostTrack(d);
    } catch {
      return null;
    }
  }
}

export interface GhostStore {
  get(stageId: string, carId: string): GhostTrack | null;
  set(stageId: string, carId: string, t: GhostTrack): void;
}

/** One localStorage entry per stage and car (~110 KB for a five-minute run), behind try/catch. */
export function ghostStore(prefix: string, storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeStorage()): GhostStore {
  const key = (s: string, c: string) => `${prefix}.${s}|${c}`;
  return {
    get(s, c) {
      try {
        const v = storage?.getItem(key(s, c));
        return v ? GhostTrack.decode(v) : null;
      } catch {
        return null;
      }
    },
    set(s, c, t) {
      try {
        storage?.setItem(key(s, c), t.encode());
      } catch {
        /* storage full or unavailable: no ghost this time */
      }
    },
  };
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}
